import { Container, ProgressBar, Form } from 'react-bootstrap';
import Stepper from 'react-stepper-horizontal/lib/Stepper';
import { PageHeader } from '../components/Layout/PageHeader';
import { DataCollectionNotice } from '../components/Layout/DataCollectionNotice';
import { BlastingAgreement } from '../components/BlastingPermit/BlastingAgreement';
import { BlastingTechnicianInfo } from '../components/BlastingPermit/BlastingTechnicianInfo';
import { BlastingInformation } from '../components/BlastingPermit/BlastingInformation';
import { BlastingSite } from '../components/BlastingPermit/BlastingSite';
import { BlastingType } from '../components/BlastingPermit/BlastingType';
import { SeismicPlan } from '../components/BlastingPermit/SeismicPlan';
import { LiabilityInsurance } from '../components/LiabilityInsurance';
import { ATFLicense } from '../components/BlastingPermit/ATFLicense';
import { ATFAdditional } from '../components/BlastingPermit/ATFAdditional';
import { useState, useEffect, useCallback } from 'react';
import { SubmitSuccess } from '../components/SubmitSuccess';
import { InternalServerError } from '../components/Errors/InternalServerError';
import { SomethingBroke } from '../components/Errors/SomethingBroke';
import FormClientError from '../components/Errors/FormClientError';
import dayjs from 'dayjs';

export function BlastingPermit() {
	const [step, setStep] = useState(1);
	const [somethingBroke, setSomethingBroke] = useState(false);
	const [submitDone, setSubmitDone] = useState(false);
	const [currBlastingInfo, setCurrBlastingInfo] = useState({
		address: '',
		state: 'UT',
		zip: '',
		city: '',
		phone: '',
		afterHourPhone: '',
		businessName: '',
		contactPerson: '',
		title: '',
		licenseNumber: '',
		jurisdiction: '',
	});
	const [currBlastingSite, setCurrBlastingSite] = useState({
		address: '',
		state: 'UT',
		zip: '',
		city: '',
		phone: '',
		businessName: '',
		projectName: '',
		contactName: '',
	});
	const [currBlastingType, setCurrBlastingType] = useState({
		permitType: '',
		date: '',
		startDate: '',
		endDate: '',
	});

	const [currBlastingTechInfo, setCurrBlastingTechInfo] = useState({
		name: '',
		state: 'UT',
		zip: '',
		city: '',
		officePhone: '',
		afterHourPhone: '',
		driversLicenseNum: '',
		stateIssuedBy: 'UT',
		dateOfBirth: '',
	});
	const [currSeismicPlan, setCurrSeismicPlan] = useState({
		file: null,
		answer: null,
	});
	const [currLiabilityInsurnace, setCurrLiabilityInsurnace] = useState({
		file: null,
		answer: null,
	});
	const [currATFLicense, setCurrATFLicense] = useState({
		file: null,
		answer: null,
	});
	const [currATFAttachments, setCurrATFAttachments] = useState({
		attachments: [{ file: {} }],
		answer: null,
	});
	const [currBlastingAgreement, setCurrBlastingAgreement] = useState({
		agreed: '',
		title: '',
		name: '',
		email: '',
	});
	const [httpStatusCode, setHttpStatusCode] = useState(null);

	const incrementStep = useCallback(() => {
		setStep(curStep => curStep + 1);
	}, []);

	const decrementStep = useCallback(() => {
		setStep(curStep => curStep - 1);
	}, []);

	const createBody = blastingAgreement => {
		const body = new FormData();
		const data = {
			blastingInfo: JSON.stringify(currBlastingInfo),
			blastingTechInfo: JSON.stringify(currBlastingTechInfo),
			blastingSite: JSON.stringify(currBlastingSite),
			blastingType: JSON.stringify(currBlastingType),
			blastingAgreement: JSON.stringify(blastingAgreement),
			atfAttachments: currATFAttachments.answer,
			atfLicense: currATFLicense.answer,
			liabilityInsurance: currLiabilityInsurnace.answer,
			seismicPlan: currSeismicPlan.answer,
			date: dayjs().format('MM/DD/YYYY'),
			name: blastingAgreement.name,
		};

		body.append('file', currATFLicense.file);
		body.append('file', currLiabilityInsurnace.file);
		for (const attachment of currATFAttachments.attachments) {
			body.append('file', attachment);
		}
		for (var key in data) {
			body.append(key, data[key]);
		}
		return body;
	};

	const submitForm = async blastingAgreement => {
		try {
			setSubmitDone(true);
			const { status } = await fetch(
				`${process.env.REACT_APP_API_PREFIX_PROTO}${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_API_PREFIX}/explosive-permit`,
				{
					method: 'POST',
					body: createBody(blastingAgreement),
				}
			);
			setHttpStatusCode(status);
			setSubmitDone(false);
		} catch (err) {
			console.error(err);
			setSubmitDone(false);

			setSomethingBroke(true);
		}
	};

	useEffect(() => {
		if (httpStatusCode === 200) {
			setHttpStatusCode(null);
			incrementStep();
		}
	}, [httpStatusCode, incrementStep]);

	const handleOnNext = (setData = () => {}) => {
		return data => {
			setData(data);
			incrementStep();
		};
	};

	const handleSubmit = (setData = () => {}) => {
		return async data => {
			setData(data);
			submitForm(data);
		};
	};

	if (somethingBroke === true) {
		return <SomethingBroke></SomethingBroke>;
	}

	if (httpStatusCode >= 500) {
		return <InternalServerError></InternalServerError>;
	}

	if (httpStatusCode >= 400) {
		return (
			<FormClientError
				returnToForm={() => {
					setStep(1);
					setHttpStatusCode(null);
				}}
			/>
		);
	}

	const steps = [
		{
			component: (
				<BlastingInformation
					key="blastInfo"
					blastingInfo={currBlastingInfo}
					onNext={handleOnNext(setCurrBlastingInfo)}
					disablePrev={true}
				/>
			),
			title: 'Company',
		},
		{
			component: (
				<BlastingTechnicianInfo
					key="blastTechInfo"
					currBlastingTechInfo={currBlastingTechInfo}
					onNext={handleOnNext(setCurrBlastingTechInfo)}
					onPrevious={decrementStep}
				/>
			),
			title: 'Technician',
		},
		{
			component: (
				<BlastingSite
					key="blastSite"
					blastingSite={currBlastingSite}
					onNext={handleOnNext(setCurrBlastingSite)}
					onPrevious={decrementStep}
				/>
			),
			title: 'Site',
		},
		{
			component: (
				<BlastingType
					key="blastType"
					blastingType={currBlastingType}
					onNext={handleOnNext(setCurrBlastingType)}
					onPrevious={decrementStep}
				/>
			),
			title: 'Permit Type',
		},
		{
			component: (
				<SeismicPlan
					key="seismicPlan"
					seismicPlan={currSeismicPlan}
					onNext={handleOnNext(setCurrSeismicPlan)}
					onPrevious={decrementStep}
				/>
			),
			title: 'Seismic Plan',
		},
		{
			component: (
				<LiabilityInsurance
					key="insuranceInfo"
					currInsurance={currLiabilityInsurnace}
					coverageAmount={'2,500,000'}
					onNext={handleOnNext(setCurrLiabilityInsurnace)}
					onPrevious={decrementStep}
				/>
			),
			title: 'Liability Insurance',
		},
		{
			component: (
				<ATFLicense
					key="atfLicense"
					currLicence={currATFLicense}
					onNext={handleOnNext(setCurrATFLicense)}
					onPrevious={decrementStep}
				/>
			),
			title: 'ATF Licence',
		},
		{
			component: (
				<ATFAdditional
					key="aftAdditional"
					currAttachments={currATFAttachments}
					onNext={handleOnNext(setCurrATFAttachments)}
					onPrevious={decrementStep}
				/>
			),
			title: 'ATF Additional Attachments',
		},
		{
			component: (
				<BlastingAgreement
					key="blastAgreement"
					currConditionon={currBlastingAgreement}
					onSubmit={handleSubmit(setCurrBlastingAgreement)}
					onPrevious={decrementStep}
					finalStep={true}
					enableLoading={submitDone}
				/>
			),
			title: 'Agreement',
		},
		{
			component: (
				<SubmitSuccess
					key="submitSuccess"
					finalStep={true}
					disablePrev={true}
					disableNext={true}
					enableLoading={submitDone}
				/>
			),
			title: 'Submission',
		},
	];

	return (
		<Container fluid className="permit p-0">
			<PageHeader title="Explosive Blasting Permit" />
			<Form className="app-container">
				<DataCollectionNotice />

				<div className="stepper blasting-stepper">
					<Stepper
						steps={steps.map(({ title }, i) => ({
							title,
							onClick: () => setStep(i + 1),
						}))}
						activeStep={step - 1}
					/>
				</div>
				<div className="blasting-bar ">
					<ProgressBar className="mb-4" now={(step / steps.length) * 100} />
				</div>
				{steps.map(({ component }, index) =>
					step === index + 1 ? component : ''
				)}
			</Form>
		</Container>
	);
}
